import { motion } from "framer-motion";
import LandingLayout from '@/components/LandingLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, MessageCircle, FileText, Github } from "lucide-react";
import { SEO } from '@/components/SEO';

const faqs = [
  {
    question: "How do I get started with StatusBot?",
    answer: "Getting started is easy! Sign up for an account, and our onboarding wizard will guide you through setting up your first project and monitor. You can also check out our comprehensive documentation for detailed guides."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, MasterCard, American Express) and support payment via PayPal. For Enterprise plans, we also offer invoice-based billing."
  },
  {
    question: "Can I change my plan later?",
    answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any payments accordingly."
  },
  {
    question: "How does the AI monitoring work?",
    answer: "Our AI monitoring system uses advanced machine learning algorithms to detect anomalies in your service performance. It learns from historical data to predict potential issues before they impact your users."
  },
  {
    question: "What kind of support do you offer?",
    answer: "We offer multiple support channels including email support, documentation, and community forums. Enterprise customers receive priority support with dedicated response times."
  }
];

const supportOptions = [
  {
    icon: <Mail className="w-6 h-6" />,
    title: "Email Support",
    description: "Get help from our support team",
    action: "Email Us",
    link: "mailto:support@status.bot"
  },
  {
    icon: <MessageCircle className="w-6 h-6" />,
    title: "Community",
    description: "Join our Discord community",
    action: "Join Discord",
    link: "https://discord.gg/statusbot"
  },
  {
    icon: <FileText className="w-6 h-6" />,
    title: "Documentation",
    description: "Browse our detailed guides",
    action: "View Docs",
    link: "/documentation"
  },
  {
    icon: <Github className="w-6 h-6" />,
    title: "GitHub",
    description: "Report issues and contribute",
    action: "Visit GitHub",
    link: "https://github.com/statusbot/statusbot"
  }
];

const Support = () => {
  return (
    <>
      <SEO 
        title="Support Center"
        description="Get help with StatusBot. Access documentation, community support, and contact our team for assistance."
      />
      <LandingLayout>
        <section className="px-4 py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="container mx-auto max-w-4xl"
          >
            <div className="space-y-8">
              <div className="text-center">
                <h1 className="text-3xl font-bold mb-2">Support Center</h1>
                <p className="text-muted-foreground">
                  Get help with StatusBot. We're here to help you succeed.
                </p>
              </div>

              {/* Support Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {supportOptions.map((option) => (
                  <Card key={option.title} className="border-2 border-violet-500/20">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="p-2 bg-violet-500/10 rounded-lg text-violet-500">
                          {option.icon}
                        </div>
                        <div className="flex-1 space-y-1">
                          <h3 className="font-semibold">{option.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                        <Button variant="ghost" onClick={() => window.location.href = option.link}>
                          {option.action}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* FAQs */}
              <Card>
                <CardHeader>
                  <CardTitle>Frequently Asked Questions</CardTitle>
                  <CardDescription>
                    Quick answers to common questions about StatusBot
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger>{faq.question}</AccordionTrigger>
                        <AccordionContent>{faq.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>

              {/* Enterprise Support */}
              <Card className="bg-violet-500/5 border-2 border-violet-500/20">
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <h3 className="text-xl font-semibold">Need Enterprise Support?</h3>
                    <p className="text-muted-foreground">
                      Get priority support, custom solutions, and dedicated resources for your organization.
                    </p>
                    <Button onClick={() => window.location.href = 'mailto:enterprise@status.bot'}>
                      Contact Enterprise Sales
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </section>
      </LandingLayout>
    </>
  );
};

export default Support; 